import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { VendorSdk } from '@libs/graphql/sdk/vendor/vendor.sdk';
import { CitySdk } from '@libs/graphql/sdk/city/city.sdk';
import { VendorUserSdk } from '@libs/graphql/sdk/vendor-user/vendor-user.sdk';
import { PartnershipSdk } from '@libs/graphql/sdk/partnership/partnership.sdk';
import type { VendorPoco, CityPoco, VendorUserPoco, PartnershipPoco } from '@libs/graphql/models';
import { SERVICE_CATEGORIES, catColor } from '@shared/utils/category.util';

import { PersonaService } from './persona.service';

/** One selectable vendor for the "Switch vendor" picker (mockup `switchModal` `.pick`). */
export interface ManagedVendorOption {
  id: number;
  name: string;
  categoryLabel: string;
  area: string;           // city name — the mockup `.pick` sub-line "category · area"
  verified: boolean;      // green ✓ / amber ! badge
  color: string;          // category colour — the `.dot` tint + icon fill
  iconCode: string;       // category code for the white-on-tint glyph (mockup `cic(v.cat)`)
}

/**
 * Loads and exposes the currently-managed vendor's identity (name / category / area)
 * for the vendor app shell chrome — the sidebar "Managing" card and the header — so
 * those stop showing hardcoded text. Also lists all vendors for the switch picker and
 * performs the switch (mockup `switchModal` / `meId`). Single root instance shared by
 * the sidebar + header. Reloads automatically whenever the managed vendor changes.
 */
@Injectable({ providedIn: 'root' })
export class ManagedVendorService {
  private readonly vendorSdk = inject(VendorSdk);
  private readonly citySdk = inject(CitySdk);
  private readonly vendorUserSdk = inject(VendorUserSdk);
  private readonly partnershipSdk = inject(PartnershipSdk);
  private readonly persona = inject(PersonaService);
  private readonly router = inject(Router);

  readonly name = signal('');
  readonly categoryLabel = signal('');
  /** The managed vendor's primary service-category code (e.g. 'hall'). */
  readonly categoryCode = signal('');
  readonly area = signal('');
  readonly verified = signal(false);
  /** The vendor's business photo (mockup `vendor.logo`) — drives the topbar profile avatar.
   *  No Vendor column yet → saved client-side by /vendor/profile (key `trexa-vp-biz-<id>`). */
  readonly logo = signal('');
  readonly loaded = signal(false);
  readonly options = signal<ManagedVendorOption[]>([]);

  /**
   * Count of incoming partnership requests awaiting THIS vendor's approval — drives the
   * "Partnerships" nav badge in the sidebar + bottom tab bar (mockup `updatePartnerBadge`
   * / `incomingRequests().length`).
   */
  readonly pendingPartnerCount = signal(0);

  /** Vendor ids the logged-in user actually owns/staffs (their VendorUser rows). */
  readonly ownedVendorIds = signal<number[]>([]);
  /** Only show "Switch vendor" when the user is linked to more than one vendor. */
  readonly canSwitch = computed(() => this.ownedVendorIds().length > 1);

  /** Sidebar sub-line, e.g. "Event Hall · Kuala Lumpur" (mockup mecard `sub`). */
  readonly sub = computed(() => [this.categoryLabel(), this.area()].filter(Boolean).join(' · '));

  private lastLoadedId: number | null = null;
  private lastOwnerUserId: number | null = null;

  constructor() {
    // Reload the identity whenever the managed vendor changes (session restore / switch).
    effect(() => {
      const id = this.persona.currentVendorId();
      if (id && id !== this.lastLoadedId) {
        this.lastLoadedId = id;
        void this.loadIdentity(id);
        void this.loadPendingPartnerCount(id);
      }
    });
    // Resolve the user's own vendors whenever the logged-in user changes.
    effect(() => {
      const uid = this.persona.currentUserId();
      if (uid && uid !== this.lastOwnerUserId) {
        this.lastOwnerUserId = uid;
        void this.loadOwnedVendorIds(uid);
      }
    });

    // Reflect a profile-photo (or service-area) change WITHOUT a page refresh. Same-tab edits
    // come through `setLogo()` when /vendor/profile saves; the `storage` event covers OTHER
    // same-origin tabs (it fires in them when the `trexa-vp-biz-<id>` key is written).
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        const id = this.persona.currentVendorId();
        if (id && e.key === 'trexa-vp-biz-' + id) {
          this.logo.set(this.savedLogo(id));
          const area = this.serviceArea(id);
          if (area) this.area.set(area);
        }
      });
    }
  }

  /** Load the VendorUser links for the current user → the vendors they may act as. */
  private async loadOwnedVendorIds(userId: number): Promise<void> {
    try {
      const res = await this.vendorUserSdk.list(
        { userId: { eq: userId } }, [{ column: 'id', desc: false }],
        ['id', 'vendorId', 'userId', 'isOwner'], 1, 500,
      );
      const ids = ((res.data as VendorUserPoco[]) ?? [])
        .map(vu => vu.vendorId).filter((v): v is number => !!v);
      this.ownedVendorIds.set(Array.from(new Set(ids)));
      // A previously-loaded (all-vendors) option list would be wrong now — reset it.
      this.options.set([]);
    } catch (err) {
      console.error('managed-vendor owned-ids load failed', err);
    }
  }

  /**
   * Count partnerships awaiting this vendor's approval: I am the partner AND status is
   * pending (mockup `incomingRequests().length`). Refreshable so the badge updates after
   * the vendor approves/rejects a request on the Partnerships page.
   */
  async loadPendingPartnerCount(vendorId: number): Promise<void> {
    try {
      const res = await this.partnershipSdk.list(
        { partnerVendorId: { eq: vendorId } }, [{ column: 'id', desc: false }],
        ['id', 'partnerVendorId', 'status', 'isDeleted'], 1, 500,
      );
      const rows = (res.data as PartnershipPoco[]) ?? [];
      this.pendingPartnerCount.set(rows.filter(p => !p.isDeleted && p.status === 'pending').length);
    } catch (err) {
      console.error('managed-vendor pending-partner count load failed', err);
    }
  }

  /** Re-read the incoming-request count for the current vendor (call after approve/reject). */
  refreshPendingPartnerCount(): void {
    const id = this.persona.currentVendorId();
    if (id) void this.loadPendingPartnerCount(id);
  }

  private categoryFor(serviceCategoryId?: number | null): string {
    return SERVICE_CATEGORIES.find(c => c.id === serviceCategoryId)?.label ?? '';
  }

  /** Load the managed vendor's name / category / area for the shell chrome. */
  async loadIdentity(vendorId: number): Promise<void> {
    try {
      const res = await this.vendorSdk.list(
        { id: { eq: vendorId } }, [{ column: 'id', desc: false }],
        ['id', 'name', 'serviceCategoryId', 'cityId', 'isVerified'], 1, 1,
      );
      const v = ((res.data as VendorPoco[]) ?? [])[0] ?? null;
      if (!v) return;
      this.name.set(v.name ?? '');
      this.categoryLabel.set(this.categoryFor(v.serviceCategoryId));
      this.categoryCode.set(this.codeFor(v.serviceCategoryId));
      this.verified.set(!!v.isVerified);
      this.logo.set(this.savedLogo(vendorId));

      // Prefer the free-text Service area saved by /vendor/profile (no Vendor column → kept
      // per-vendor in localStorage), else fall back to the DB city name.
      const serviceArea = this.serviceArea(vendorId);
      if (serviceArea) {
        this.area.set(serviceArea);
      } else if (v.cityId) {
        const c = await this.citySdk.list({ id: { eq: v.cityId } }, [{ column: 'id', desc: false }], ['id', 'name'], 1, 1);
        this.area.set(((c.data as CityPoco[]) ?? [])[0]?.name ?? '');
      } else {
        this.area.set('');
      }
      this.loaded.set(true);
    } catch (err) {
      console.error('managed-vendor identity load failed', err);
    }
  }

  /** The vendor's free-text Service area, saved client-side by /vendor/profile
   *  (key `trexa-vp-biz-<vendorId>`), else '' → fall back to the DB city name. */
  private serviceArea(vendorId: number): string {
    try {
      const raw = localStorage.getItem('trexa-vp-biz-' + vendorId);
      return raw ? String(JSON.parse(raw).area ?? '').trim() : '';
    } catch { return ''; }
  }

  /** The vendor's saved business photo (same `trexa-vp-biz-<id>` key as the service area). */
  private savedLogo(vendorId: number): string {
    try {
      const raw = localStorage.getItem('trexa-vp-biz-' + vendorId);
      return raw ? String(JSON.parse(raw).logo ?? '') : '';
    } catch { return ''; }
  }

  /** Update the shared avatar photo after /vendor/profile saves it (keeps the shell in sync
   *  without a full reload). */
  setLogo(url: string): void { this.logo.set(url ?? ''); }

  /** Update the shared Service area after /vendor/profile saves it, so the sidebar sub-line
   *  reflects it immediately in THIS tab (the `storage` listener only reaches OTHER tabs).
   *  Pass the freshly-saved free-text area; blank falls back to the DB city name. */
  setArea(area: string): void {
    const next = (area ?? '').trim();
    if (next) { this.area.set(next); return; }
    // Cleared → re-resolve the fallback exactly like loadIdentity (DB city name, else blank).
    const id = this.persona.currentVendorId();
    if (id) void this.loadIdentity(id);
  }

  private codeFor(serviceCategoryId?: number | null): string {
    return SERVICE_CATEGORIES.find(c => c.id === serviceCategoryId)?.code ?? '';
  }

  /**
   * Lazy-load the switch picker — scoped to ONLY the vendors this user is linked to
   * (their VendorUser rows), never all vendors. A vendor can't switch to / view /
   * manage a vendor they aren't linked to. The API additionally scopes vendor-data
   * queries by the caller's VendorUser.
   */
  async loadOptions(): Promise<void> {
    if (this.options().length) return;

    // Make sure we know which vendors this user owns before building the picker.
    if (!this.ownedVendorIds().length) {
      const uid = this.persona.currentUserId();
      if (uid) await this.loadOwnedVendorIds(uid);
    }
    const owned = new Set(this.ownedVendorIds());
    if (!owned.size) { this.options.set([]); return; }

    try {
      const [vres, cres] = await Promise.all([
        this.vendorSdk.list(
          {}, [{ column: 'name', desc: false }],
          ['id', 'name', 'serviceCategoryId', 'cityId', 'isVerified'], 1, 5000,
        ),
        this.citySdk.list({}, [{ column: 'id', desc: false }], ['id', 'name'], 1, 5000),
      ]);
      const cityName = new Map<number, string>();
      ((cres.data as CityPoco[]) ?? []).forEach(c => { if (c.id) cityName.set(c.id, c.name ?? ''); });

      const opts = ((vres.data as VendorPoco[]) ?? []).filter(v => owned.has(v.id)).map(v => {
        const code = this.codeFor(v.serviceCategoryId);
        const color = catColor(code);
        return {
          id: v.id,
          name: v.name ?? '—',
          categoryLabel: this.categoryFor(v.serviceCategoryId),
          area: this.serviceArea(v.id) || (v.cityId ? (cityName.get(v.cityId) ?? '') : ''),
          verified: !!v.isVerified,
          color,
          iconCode: code,
        };
      });
      this.options.set(opts);
    } catch (err) {
      console.error('managed-vendor options load failed', err);
    }
  }

  /** Switch the managed vendor (mockup `switchModal` → `meId`) and return to the dashboard. */
  switchTo(vendorId: number): void {
    if (vendorId === this.persona.currentVendorId()) return;
    this.persona.setVendor(vendorId);         // updates currentVendorId + persists → identity effect reloads the shell
    const target = '/vendor/dashboard';
    if (this.router.url.split('?')[0] === target) {
      // Already on the dashboard: the router won't re-init the component, so hard-reload
      // to pick up the new managed vendor cleanly (persona was persisted above).
      window.location.assign(target);
    } else {
      void this.router.navigateByUrl(target);
    }
  }
}
